import React, { useEffect, useCallback, useState, useMemo, useRef } from 'react';
import { Input, Button, Modal, message, Popconfirm, Select, Tooltip, Switch } from 'antd';
import {
  Users, Plus, Wallet, Search, ArrowLeft, Edit3, Lock, Key,
  Power, PowerOff, Send, ArrowDownLeft, Eye, EyeOff, Copy,
  Trash2, Clock, TrendingUp, DollarSign, AlertTriangle, ChevronRight,
  RefreshCw, User, Mail, XCircle, RotateCw, CheckCircle, Check, Minus,
} from 'lucide-react';
import { useNavigate } from 'react-router';
import { clientActions } from '../../../actions/client';
import { useTheme } from '../../../contexts/ThemeContext';
import { apiErrorText } from '../../../utils/apiError';

// Order-independent signature of a usage-access selection, so "is this dirty?"
// doesn't depend on the order checkboxes happened to be clicked in.
const usageSnapshot = (scope, accounts, keys) =>
  scope !== 'selected'
    ? scope
    : `selected|${[...accounts].sort().join(',')}|${[...keys].sort().join(',')}`;

// The explainer addresses the member by name — "Ali sees…" lands harder than
// "this sub-account sees…".
const firstName = (name) => (String(name || '').trim().split(/\s+/)[0] || 'This member');

function SubAccountsScreen() {
  const navigate = useNavigate();
  const { colors } = useTheme();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);


  // ─── State ───
  const [subAccounts, setSubAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchVal, setSearchVal] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Create sub-account modal
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ email: '', name: '', password: '', daily_limit: '', monthly_limit: '', can_manage_api_keys: false });
  const [createLoading, setCreateLoading] = useState(false);
  const [showCreatePw, setShowCreatePw] = useState(false);

  // Detail / Edit modal
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedSub, setSelectedSub] = useState(null);
  // Mirrors the open member's id for async handlers that must not apply a
  // late-landing result to whichever member is on screen by then.
  // Assigned synchronously in handleOpenDetail rather than from an effect: a
  // passive effect flushes after commit, while an awaited PUT resolves on the
  // microtask queue, so an effect would leave a window where this still holds
  // the previous member.
  const selectedSubRef = useRef(null);
  const [detailTab, setDetailTab] = useState('overview'); // overview | budget | apikeys | password

  // Budget edit
  const [budgetForm, setBudgetForm] = useState({ daily_limit: '', monthly_limit: '' });
  const [budgetSaving, setBudgetSaving] = useState(false);

  // Allocate funds modal
  const [allocateModalOpen, setAllocateModalOpen] = useState(false);
  const [allocateSubId, setAllocateSubId] = useState(null);
  const [allocateAmount, setAllocateAmount] = useState('');
  const [allocateLoading, setAllocateLoading] = useState(false);

  // Recall funds modal
  const [recallModalOpen, setRecallModalOpen] = useState(false);
  const [recallSubId, setRecallSubId] = useState(null);
  const [recallAmount, setRecallAmount] = useState('');
  const [recallLoading, setRecallLoading] = useState(false);

  // Password reset
  const [newPassword, setNewPassword] = useState('');
  const [showNewPw, setShowNewPw] = useState(false);
  const [pwSaving, setPwSaving] = useState(false);

  // API Keys for sub-account
  const [subApiKeys, setSubApiKeys] = useState([]);
  const [subApiKeysLoading, setSubApiKeysLoading] = useState(false);
  const [newSubKeyName, setNewSubKeyName] = useState('');
  const [creatingSubKey, setCreatingSubKey] = useState(false);
  const [keyPermSaving, setKeyPermSaving] = useState(false);
  // Set only after a successful revoke, to explain what revoking did NOT do:
  // the member's existing keys stay live. Cleared per member in handleOpenDetail.
  const [keysRevokedNotice, setKeysRevokedNotice] = useState(false);
  const [createdSubKey, setCreatedSubKey] = useState(null);
  const [showCreatedKeyModal, setShowCreatedKeyModal] = useState(false);

  // Usage access (which usage this sub-account may see beyond its own)
  const [usageScope, setUsageScope] = useState('own'); // own | selected | all
  const [usageAccounts, setUsageAccounts] = useState([]); // granted client_ids
  const [usageKeys, setUsageKeys] = useState([]); // granted client_api_key_ids
  const [usageInventory, setUsageInventory] = useState(null);
  const [usageLoading, setUsageLoading] = useState(false);
  const [usageSaving, setUsageSaving] = useState(false);
  const [usageError, setUsageError] = useState(false);
  // Which sub-account the loaded state belongs to — guards against refetching
  // (and so discarding unsaved picks) every time the tab is re-selected.
  const [usageAccessFor, setUsageAccessFor] = useState(null);
  const [usageBaseline, setUsageBaseline] = useState('');
  const [expandedAccounts, setExpandedAccounts] = useState({});
  const [usageSearch, setUsageSearch] = useState('');
  const usageReqRef = useRef(0);

  // Main account balance
  const [mainBalance, setMainBalance] = useState(null);

  // Transfer history
  const [transfersModalOpen, setTransfersModalOpen] = useState(false);
  const [transfers, setTransfers] = useState([]);
  const [transfersLoading, setTransfersLoading] = useState(false);

  // Invite existing account
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', daily_limit: '', monthly_limit: '' });
  const [inviteLoading, setInviteLoading] = useState(false);
  const [sentInvitations, setSentInvitations] = useState([]);

  // ─── Fetch ───
  const fetchSubAccounts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await clientActions.getSubAccounts();
      setSubAccounts(res?.data ?? res ?? []);
    } catch {
      setSubAccounts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSentInvitations = useCallback(async () => {
    try {
      const res = await clientActions.getSentInvitations();
      setSentInvitations(Array.isArray(res) ? res : res?.data ?? []);
    } catch {
      setSentInvitations([]);
    }
  }, []);

  const fetchMainBalance = useCallback(async () => {
    try {
      const res = await clientActions.getSpendingOverview();
      const balance = res?.data?.main_account?.client_balance ?? res?.main_account?.client_balance;
      setMainBalance(Number(balance || 0));
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    fetchSubAccounts();
    fetchSentInvitations();
    fetchMainBalance();
  }, [fetchSubAccounts, fetchSentInvitations, fetchMainBalance]);

  // ─── Derived ───
  const filtered = useMemo(() => {
    const list = Array.isArray(subAccounts) ? subAccounts : [];
    const s = searchVal.toLowerCase();
    return list.filter((a) => {
      const matchSearch = !s || (a.client_name || '').toLowerCase().includes(s) || (a.client_email || '').toLowerCase().includes(s);
      // The list only ever contains non-deleted rows, so "inactive" means
      // is_active = 0. Reading `a.deleted` here made the Inactive filter always
      // return zero rows.
      const matchStatus = statusFilter === 'all' || (statusFilter === 'active' ? !!a.is_active : !a.is_active);
      return matchSearch && matchStatus;
    });
  }, [subAccounts, searchVal, statusFilter]);

  const totalBalance = useMemo(() => {
    const list = Array.isArray(subAccounts) ? subAccounts : [];
    return list.reduce((sum, a) => sum + Number(a.client_balance || 0), 0);
  }, [subAccounts]);

  // ─── Handlers ───
  const handleCreate = async () => {
    const { email, name, password } = createForm;
    if (!email || !name || !password) {
      message.warning('Email, name, and password are required');
      return;
    }
    if (password.length < 6) {
      message.warning('Password must be at least 6 characters');
      return;
    }
    setCreateLoading(true);
    try {
      await clientActions.createSubAccount({
        email: createForm.email,
        name: createForm.name,
        password: createForm.password,
        daily_limit: createForm.daily_limit ? Number(createForm.daily_limit) : null,
        monthly_limit: createForm.monthly_limit ? Number(createForm.monthly_limit) : null,
        can_manage_api_keys: !!createForm.can_manage_api_keys,
      });
      message.success('Sub-account created');
      setCreateModalOpen(false);
      setCreateForm({ email: '', name: '', password: '', daily_limit: '', monthly_limit: '', can_manage_api_keys: false });
      fetchSubAccounts();
    } catch (err) {
      message.error(apiErrorText(err, 'Failed to create sub-account'));
    } finally {
      setCreateLoading(false);
    }
  };

  const handleInvite = async () => {
    if (!inviteForm.email) {
      message.warning('Email is required');
      return;
    }
    setInviteLoading(true);
    try {
      await clientActions.sendInvitation({
        email: inviteForm.email,
        daily_limit: inviteForm.daily_limit ? Number(inviteForm.daily_limit) : null,
        monthly_limit: inviteForm.monthly_limit ? Number(inviteForm.monthly_limit) : null,
      });
      message.success('Invitation sent');
      setInviteModalOpen(false);
      setInviteForm({ email: '', daily_limit: '', monthly_limit: '' });
      fetchSentInvitations();
    } catch (err) {
      message.error(apiErrorText(err, 'Failed to send invitation'));
    } finally {
      setInviteLoading(false);
    }
  };

  const handleCancelInvitation = async (invitationId) => {
    try {
      await clientActions.cancelInvitation(invitationId);
      message.success('Invitation cancelled');
      fetchSentInvitations();
    } catch (err) {
      message.error(apiErrorText(err, 'Failed to cancel invitation'));
    }
  };

  const handleResendInvitation = async (invitationId) => {
    try {
      await clientActions.resendInvitation(invitationId);
      message.success('Invitation resent');
      fetchSentInvitations();
    } catch (err) {
      message.error(apiErrorText(err, 'Failed to resend invitation'));
    }
  };

  const handleToggleActive = async (sub) => {
    try {
      // Toggle off the CURRENT state. This used to read `sub.deleted`, which
      // getSubAccounts never selects (it filters deleted:0 and returns only
      // is_active) — so it was always undefined, and "Deactivate" always sent
      // is_active:1 while toasting "deactivated". It re-activated instead.
      await clientActions.updateSubAccount(sub.client_id, {
        is_active: sub.is_active ? 0 : 1,
      });
      message.success(sub.is_active ? 'Sub-account deactivated' : 'Sub-account activated');
      fetchSubAccounts();
    } catch (err) {
      message.error(apiErrorText(err, 'Failed to update sub-account'));
    }
  };

  const handleOpenDetail = async (sub) => {
    selectedSubRef.current = sub?.client_id ?? null;
    setSelectedSub(sub);
    setDetailTab('overview');
    setBudgetForm({
      daily_limit: sub.daily_limit ?? '',
      monthly_limit: sub.monthly_limit ?? '',
    });
    setNewPassword('');
    setSubApiKeys([]);
    setKeysRevokedNotice(false);
    setKeyPermSaving(false);
    // Drop every bit of usage-access state belonging to the previously opened
    // member — leaving any of it behind would render one member's configuration
    // while editing another. Bumping the token also invalidates any request
    // still in flight for the previous member.
    usageReqRef.current += 1;
    setUsageAccessFor(null);
    setUsageInventory(null);
    setUsageScope('own');
    setUsageAccounts([]);
    setUsageKeys([]);
    setUsageBaseline('own');
    setUsageSearch('');
    setUsageError(false);
    setExpandedAccounts({});
    setDetailModalOpen(true);
  };

  const handleSaveBudget = async () => {
    if (!selectedSub) return;
    setBudgetSaving(true);
    try {
      await clientActions.updateSubAccountBudget(selectedSub.client_id, {
        daily_limit: budgetForm.daily_limit !== '' ? Number(budgetForm.daily_limit) : null,
        monthly_limit: budgetForm.monthly_limit !== '' ? Number(budgetForm.monthly_limit) : null,
      });
      message.success('Budget updated');
      fetchSubAccounts();
    } catch (err) {
      message.error(apiErrorText(err, 'Failed to update budget'));
    } finally {
      setBudgetSaving(false);
    }
  };

  const handleResetPassword = async () => {
    if (!selectedSub || !newPassword || newPassword.length < 6) {
      message.warning('Password must be at least 6 characters');
      return;
    }
    setPwSaving(true);
    try {
      await clientActions.resetSubAccountPassword(selectedSub.client_id, { password: newPassword });
      message.success('Password reset successfully');
      setNewPassword('');
    } catch (err) {
      message.error(apiErrorText(err, 'Failed to reset password'));
    } finally {
      setPwSaving(false);
    }
  };

  const handleAllocate = async () => {
    const amt = Number(allocateAmount);
    if (!amt || amt <= 0) {
      message.warning('Enter a valid amount');
      return;
    }
    setAllocateLoading(true);
    try {
      await clientActions.allocateFunds([{ sub_client_id: allocateSubId, amount: amt }]);
      message.success(`Allocated ${amt} URO`);
      setAllocateModalOpen(false);
      setAllocateAmount('');
      fetchSubAccounts();
      fetchMainBalance();
    } catch (err) {
      message.error(apiErrorText(err, 'Failed to allocate funds'));
    } finally {
      setAllocateLoading(false);
    }
  };

  const handleRecall = async () => {
    const amt = Number(recallAmount);
    if (!amt || amt <= 0) {
      message.warning('Enter a valid amount');
      return;
    }
    setRecallLoading(true);
    try {
      await clientActions.recallFunds(recallSubId, { amount: amt });
      message.success(`Recalled ${amt} URO`);
      setRecallModalOpen(false);
      setRecallAmount('');
      fetchSubAccounts();
      fetchMainBalance();
    } catch (err) {
      message.error(apiErrorText(err, 'Failed to recall funds'));
    } finally {
      setRecallLoading(false);
    }
  };

  // API Keys for sub-account
  const fetchSubApiKeys = async (subId) => {
    setSubApiKeysLoading(true);
    try {
      const res = await clientActions.getSubAccountApiKeys(subId);
      // Guard the shape HERE rather than at each render site: the unwrap above
      // can legitimately yield a non-array, and the revoke panel now renders a
      // count off this state.
      const rows = res?.data ?? res ?? [];
      setSubApiKeys(Array.isArray(rows) ? rows : []);
    } catch {
      setSubApiKeys([]);
    } finally {
      setSubApiKeysLoading(false);
    }
  };

  // ─── Usage access ───

  // Loads the org-wide key inventory and this sub-account's current grants
  // together, so the picker can render its checked state in one pass.
  const fetchUsageAccess = async (subId) => {
    // Request token. Without it, opening member A, closing, then opening member
    // B can land A's in-flight response onto B: every setState below would apply
    // while selectedSub is B, usageDirty would read false, and the next Save
    // would PUT A's grants to B with no visual tell (the inventory is identical
    // for both).
    const reqId = ++usageReqRef.current;
    setUsageLoading(true);
    setUsageError(false);
    try {
      const [inventory, current] = await Promise.all([
        clientActions.getUsageAccessInventory(),
        clientActions.getSubAccountUsageAccess(subId),
      ]);
      if (reqId !== usageReqRef.current) return; // superseded by a newer request
      const inv = inventory?.data ?? inventory ?? null;
      const cur = current?.data ?? current ?? {};
      const grants = Array.isArray(cur.grants) ? cur.grants : [];
      const accounts = grants.filter((g) => g.scope_type === 'account').map((g) => g.scope_id);
      const keys = grants.filter((g) => g.scope_type === 'api_key').map((g) => g.scope_id);

      const ownerOf = {};
      for (const acct of [inv?.main_account, ...(inv?.sub_accounts || [])].filter(Boolean)) {
        for (const k of acct.api_keys || []) ownerOf[k.client_api_key_id] = acct.client_id;
      }

      setUsageInventory(inv);
      setUsageScope(cur.usage_scope || 'own');
      setUsageAccounts(accounts);
      setUsageKeys(keys);
      // Baseline must be measured the same way the dirty check is — i.e. after
      // account-covered keys are stripped — or the tab opens already claiming
      // unsaved changes.
      setUsageBaseline(usageSnapshot(
        cur.usage_scope || 'own',
        accounts,
        keys.filter((keyId) => !accounts.includes(ownerOf[keyId]))
      ));
      setUsageAccessFor(subId);
      setUsageSearch('');

      // Surface existing configuration instead of making them hunt for it.
      const expand = {};
      for (const keyId of keys) if (ownerOf[keyId]) expand[ownerOf[keyId]] = true;
      setExpandedAccounts(expand);
    } catch (err) {
      if (reqId !== usageReqRef.current) return;
      // Deliberately do NOT set usageScope here. Forcing it to 'own' would
      // display "Own usage only" as this member's current setting when they may
      // hold a broader grant, and — because 'own' is never "nothing selected" —
      // would leave Save enabled on a payload that wipes every grant they have.
      // usageError alone drives the UI; Save stays disabled while it is set.
      setUsageInventory(null);
      setUsageError(true);
      message.error(apiErrorText(err, 'Failed to load usage access'));
    } finally {
      if (reqId === usageReqRef.current) setUsageLoading(false);
    }
  };

  const toggleIn = (list, value) =>
    list.includes(value) ? list.filter((v) => v !== value) : [...list, value];

  // Owner lookup, used to strip key grants that a whole-account grant already covers.
  const keyOwnerById = useMemo(() => {
    const map = {};
    if (!usageInventory) return map;
    for (const acct of [usageInventory.main_account, ...(usageInventory.sub_accounts || [])].filter(Boolean)) {
      for (const k of acct.api_keys || []) map[k.client_api_key_id] = acct.client_id;
    }
    return map;
  }, [usageInventory]);

  // Individual key picks are kept in UI state even while their account is
  // checked — the account grant supersedes them visually, and unchecking it
  // brings them back rather than silently discarding the user's work. The
  // redundancy is stripped once, at save time.
  const effectiveKeyGrants = useMemo(
    () => usageKeys.filter((keyId) => !usageAccounts.includes(keyOwnerById[keyId])),
    [usageKeys, usageAccounts, keyOwnerById]
  );

  // Accounts in picker order (self first, then main, then siblings), filtered by
  // the search box. `visibleKeys` is what the expanded group renders; `api_keys`
  // stays intact so counts always describe the account, not the filter.
  const usageVisibleAccounts = useMemo(() => {
    if (!usageInventory) return [];
    const all = [
      { ...usageInventory.main_account, isMain: true },
      ...(usageInventory.sub_accounts || []),
    ].filter((a) => a && a.client_id);

    const selfId = selectedSub?.client_id;
    all.sort((a, b) => {
      if (a.client_id === selfId) return -1;
      if (b.client_id === selfId) return 1;
      if (a.isMain) return -1;
      if (b.isMain) return 1;
      return String(a.client_name || '').localeCompare(String(b.client_name || ''));
    });

    const q = usageSearch.trim().toLowerCase();
    if (!q) return all.map((a) => ({ ...a, visibleKeys: a.api_keys || [], keyMatched: false }));

    const out = [];
    for (const acct of all) {
      // The self row is context, not a search result — never filter it away.
      if (acct.client_id === selfId) {
        out.push({ ...acct, visibleKeys: acct.api_keys || [], keyMatched: false });
        continue;
      }
      const accountHit =
        String(acct.client_name || '').toLowerCase().includes(q) ||
        String(acct.client_email || '').toLowerCase().includes(q);
      const matchedKeys = (acct.api_keys || []).filter((k) =>
        String(k.api_key_name || '').toLowerCase().includes(q) ||
        String(k.description || '').toLowerCase().includes(q) ||
        String(k.api_key_prefix || '').toLowerCase().includes(q)
      );
      if (accountHit) out.push({ ...acct, visibleKeys: acct.api_keys || [], keyMatched: false });
      else if (matchedKeys.length) out.push({ ...acct, visibleKeys: matchedKeys, keyMatched: true });
    }
    return out;
  }, [usageInventory, usageSearch, selectedSub]);

  const usageTotalKeys = useMemo(() => {
    if (!usageInventory) return 0;
    return [usageInventory.main_account, ...(usageInventory.sub_accounts || [])]
      .filter(Boolean)
      .reduce((n, a) => n + (a.api_keys?.length || 0), 0);
  }, [usageInventory]);

  // Open member's key-management permission, and their name for the copy that
  // explains it. Absent field (uro-web deployed ahead of uro-api) reads as
  // false, which is exactly the pre-feature truth: no sub could manage keys.
  const subCanManageKeys = Boolean(selectedSub?.can_manage_api_keys);
  const subFirstName = firstName(selectedSub?.client_name);

  // Keys this member minted themselves. Revoking the permission leaves these
  // live, so they are the ones worth reviewing — keys the parent created (or
  // legacy keys with no attribution) were never the member's to begin with.
  const subSelfMintedKeys = subApiKeys.filter(
    (k) => k.created_by_client_id && k.created_by_client_id === selectedSub?.client_id
  );

  const usageDirty = usageSnapshot(usageScope, usageAccounts, effectiveKeyGrants) !== usageBaseline;
  const nothingSelected =
    usageScope === 'selected' && !usageAccounts.length && !effectiveKeyGrants.length;

  // Save is a FULL REPLACE, so it must never fire against state that wasn't
  // actually loaded from the server for THIS member — a failed or superseded
  // load would otherwise let one click wipe every grant the member has.
  const usageSaveBlocked =
    !usageDirty ||
    nothingSelected ||
    usageLoading ||
    usageError ||
    usageSaving ||
    usageAccessFor !== selectedSub?.client_id;

  const handleSaveUsageAccess = async () => {
    if (!selectedSub) return;
    setUsageSaving(true);
    try {
      const grants =
        usageScope === 'selected'
          ? [
              ...usageAccounts.map((id) => ({ scope_type: 'account', scope_id: id })),
              ...effectiveKeyGrants.map((id) => ({ scope_type: 'api_key', scope_id: id })),
            ]
          : [];
      await clientActions.updateSubAccountUsageAccess(selectedSub.client_id, {
        usage_scope: usageScope,
        grants,
      });
      message.success('Usage access updated');
      if (usageScope === 'selected') {
        setUsageKeys(effectiveKeyGrants);
      } else {
        // The server cleared the list for 'own'/'all'. Mirror that, or flipping
        // back to Selected would re-present checkboxes for grants that no longer
        // exist and Save would silently re-create them.
        setUsageAccounts([]);
        setUsageKeys([]);
      }
      setUsageBaseline(usageSnapshot(
        usageScope,
        usageScope === 'selected' ? usageAccounts : [],
        usageScope === 'selected' ? effectiveKeyGrants : []
      ));
    } catch (err) {
      message.error(apiErrorText(err, 'Failed to update usage access'));
    } finally {
      setUsageSaving(false);
    }
  };

  const handleCreateSubApiKey = async () => {
    if (!newSubKeyName?.trim() || !selectedSub) return;
    setCreatingSubKey(true);
    try {
      const res = await clientActions.createSubAccountApiKey(selectedSub.client_id, { name: newSubKeyName });
      setNewSubKeyName('');
      setCreatedSubKey(res?.api_key || res?.data?.api_key || null);
      setShowCreatedKeyModal(true);
      fetchSubApiKeys(selectedSub.client_id);
    } catch (err) {
      message.error(apiErrorText(err, 'Failed to create API key'));
    } finally {
      setCreatingSubKey(false);
    }
  };

  const handleDeleteSubApiKey = async (keyId) => {
    if (!selectedSub) return;
    try {
      await clientActions.deleteSubAccountApiKey(selectedSub.client_id, keyId);
      message.success('API key removed');
      fetchSubApiKeys(selectedSub.client_id);
    } catch (err) {
      message.error(apiErrorText(err, 'Failed to remove API key'));
    }
  };

  // Grant/revoke this member's ability to manage its OWN API keys.
  //
  // Deliberately NOT optimistic: the Switch's `checked` is bound to the saved
  // value and it has no onChange, so the thumb cannot move until the server
  // returns 200. A permission toggle that animates and then snaps back has
  // shown the admin a state that was never true.
  //
  // `selectedSub` is a captured copy of the list row and nothing refetches it,
  // so patch it and the list together. Refetching the list instead would leave
  // the open modal showing the stale value.
  const handleToggleKeyPermission = async (next) => {
    if (!selectedSub || keyPermSaving) return;
    // Pin the member this call is FOR. Everything below is guarded on it:
    // toggle Ali, close, open Sara before the PUT resolves, and an unguarded
    // setSelectedSub would write Ali's new permission onto Sara's open modal
    // (and fire Sara's revoke panel) while the toast named Ali.
    const targetId = selectedSub.client_id;
    const who = firstName(selectedSub.client_name);
    setKeyPermSaving(true);
    try {
      await clientActions.updateSubAccount(targetId, { can_manage_api_keys: next });
      const stored = !!next; // the API returns this field as a boolean
      setSubAccounts((list) =>
        list.map((s) => (s.client_id === targetId ? { ...s, can_manage_api_keys: stored } : s))
      );
      setSelectedSub((prev) =>
        prev?.client_id === targetId ? { ...prev, can_manage_api_keys: stored } : prev
      );
      // Revoking blocks future changes but leaves already-created keys live;
      // the amber panel below the toggle is what actually says so. Only show it
      // if that member is still the one on screen.
      if (selectedSubRef.current === targetId) setKeysRevokedNotice(!next);
      message.success(
        next
          ? `${who} can now manage their own API keys`
          : `${who} can no longer create or delete API keys`
      );
    } catch (err) {
      message.error(apiErrorText(err, 'Failed to update key permission'));
    } finally {
      setKeyPermSaving(false);
    }
  };

  // Transfers
  const handleOpenTransfers = async () => {
    setTransfersModalOpen(true);
    setTransfersLoading(true);
    try {
      const res = await clientActions.getTransferHistory({ page: 1, limit: 50 });
      setTransfers(res?.data?.transfers ?? res?.transfers ?? []);
    } catch {
      setTransfers([]);
    } finally {
      setTransfersLoading(false);
    }
  };

  // ─── Styles ───
  const cardStyle = {
    background: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: 16,
    overflow: 'hidden',
  };
  const sp = isMobile ? 16 : 24;
  const inputStyle = {
    width: '100%',
    padding: '10px 14px',
    borderRadius: 10,
    border: `1px solid ${colors.border}`,
    background: 'transparent',
    color: colors.text,
    fontSize: 14,
    outline: 'none',
    boxSizing: 'border-box',
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    const parts = String(name).trim().split(/\s+/);
    const first = parts[0]?.[0] || '';
    const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
    return (first + last).toUpperCase() || 'U';
  };

  return (
    <div style={{ height: '100%', overflow: 'auto', background: colors.background }}>
      <div
        style={{
          maxWidth: 1100,
          margin: '0 auto',
          padding: isMobile ? '20px 16px 40px' : '32px 24px 60px',
          display: 'flex',
          flexDirection: 'column',
          gap: isMobile ? 16 : 20,
        }}
      >
        {/* Page header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button
              onClick={() => navigate('/account')}
              style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                border: `1px solid ${colors.border}`,
                background: 'transparent',
                color: colors.text,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.15s ease',
              }}
            >
              <ArrowLeft size={16} />
            </button>
            <div>
              <h1 style={{ fontSize: isMobile ? 22 : 26, fontWeight: 700, color: colors.text, margin: 0, letterSpacing: '-0.02em' }}>
                Sub-Accounts
              </h1>
              <p style={{ fontSize: 14, color: colors.textSecondary, margin: '2px 0 0' }}>
                Manage team members and their budgets
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={handleOpenTransfers}
              className="sub-action-btn"
              style={{
                height: 38,
                padding: '0 14px',
                borderRadius: 10,
                border: `1px solid ${colors.border}`,
                background: 'transparent',
                color: colors.text,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 13,
                fontWeight: 500,
                transition: 'all 0.15s ease',
              }}
            >
              <Clock size={14} />
              Transfers
            </button>
            <button
              onClick={() => {
                setInviteForm({ email: '', daily_limit: '', monthly_limit: '' });
                setInviteModalOpen(true);
              }}
              className="sub-action-btn"
              style={{
                height: 38,
                padding: '0 14px',
                borderRadius: 10,
                border: `1px solid #6366F1`,
                background: 'transparent',
                color: '#6366F1',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 13,
                fontWeight: 600,
                transition: 'all 0.15s ease',
              }}
            >
              <Mail size={14} />
              Invite Existing
            </button>
            <button
              onClick={() => {
                setCreateForm({ email: '', name: '', password: '', daily_limit: '', monthly_limit: '' });
                setShowCreatePw(false);
                setCreateModalOpen(true);
              }}
              style={{
                height: 38,
                padding: '0 16px',
                borderRadius: 10,
                border: 'none',
                background: '#6366F1',
                color: '#fff',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 13,
                fontWeight: 600,
                transition: 'all 0.15s ease',
              }}
            >
              <Plus size={14} />
              Add New
            </button>
          </div>
        </div>

        {/* Stats row */}
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr 1fr 1fr 1fr', gap: 12 }}>
          {[
            ...(mainBalance != null ? [{
              label: 'Total Balance',
              value: `${(mainBalance + totalBalance).toFixed(2)} URO`,
              icon: <DollarSign size={16} color="#6366F1" />,
              bg: 'rgba(99,102,241,0.1)',
            }] : []),
            {
              label: 'Main Account',
              value: mainBalance != null ? `${mainBalance.toFixed(2)} URO` : '—',
              icon: <Wallet size={16} color="#3B82F6" />,
              bg: 'rgba(59,130,246,0.1)',
            },
            {
              label: 'Allocated to Subs',
              value: `${totalBalance.toFixed(2)} URO`,
              icon: <Users size={16} color="#10B981" />,
              bg: 'rgba(16,185,129,0.1)',
            },
            {
              label: `${(Array.isArray(subAccounts) ? subAccounts : []).length} Members`,
              value: `${(Array.isArray(subAccounts) ? subAccounts : []).filter((a) => a.is_active).length} active`,
              icon: <Power size={16} color="#F59E0B" />,
              bg: 'rgba(245,158,11,0.1)',
            },
          ].map((stat, i) => (
            <div key={i} style={cardStyle}>
              <div style={{ padding: '16px 18px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <div style={{ width: 30, height: 30, borderRadius: 8, background: stat.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {stat.icon}
                  </div>
                  <span style={{ fontSize: 11, color: colors.textSecondary, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    {stat.label}
                  </span>
                </div>
                <span style={{ fontSize: 22, fontWeight: 700, color: colors.text, letterSpacing: '-0.02em' }}>
                  {stat.value}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <Input
            allowClear
            placeholder="Search members..."
            prefix={<Search size={14} style={{ color: colors.textSecondary }} />}
            value={searchVal}
            onChange={(e) => setSearchVal(e.target.value)}
            style={{ maxWidth: isMobile ? '100%' : 280, flex: isMobile ? 1 : undefined, background: 'transparent', borderRadius: 8 }}
          />
          <Select
            size="middle"
            value={statusFilter}
            onChange={setStatusFilter}
            style={{ width: 130 }}
            options={[
              { value: 'all', label: 'All Status' },
              { value: 'active', label: 'Active' },
              { value: 'inactive', label: 'Disabled' },
            ]}
          />
          <button
            onClick={fetchSubAccounts}
            style={{
              width: 38,
              height: 38,
              borderRadius: 10,
              border: `1px solid ${colors.border}`,
              background: 'transparent',
              color: colors.textSecondary,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <RefreshCw size={14} />
          </button>
        </div>

        {/* Sub-accounts list */}
        {loading ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                style={{
                  height: 80,
                  borderRadius: 14,
                  background: `${colors.text}04`,
                  border: `1px solid ${colors.border}`,
                  animation: 'sub-shimmer 1.5s infinite',
                  backgroundSize: '200% 100%',
                  backgroundImage: `linear-gradient(90deg, ${colors.surface} 0%, ${colors.border} 50%, ${colors.surface} 100%)`,
                }}
              />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ ...cardStyle, textAlign: 'center', padding: '48px 16px' }}>
            <Users size={32} style={{ color: colors.textSecondary, opacity: 0.4, marginBottom: 12 }} />
            <div style={{ fontSize: 15, fontWeight: 500, color: colors.text, marginBottom: 4 }}>
              {searchVal || statusFilter !== 'all' ? 'No matching members' : 'No sub-accounts yet'}
            </div>
            <div style={{ fontSize: 13, color: colors.textSecondary, marginBottom: 20 }}>
              {searchVal || statusFilter !== 'all' ? 'Try adjusting your filters' : 'Create your first team member to get started'}
            </div>
            {!searchVal && statusFilter === 'all' && (
              <button
                onClick={() => setCreateModalOpen(true)}
                style={{
                  padding: '8px 20px',
                  borderRadius: 8,
                  border: 'none',
                  background: '#6366F1',
                  color: '#fff',
                  cursor: 'pointer',
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                <Plus size={13} style={{ marginRight: 6, verticalAlign: 'middle' }} />
                Add Member
              </button>
            )}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {filtered.map((sub) => (
              <div
                key={sub.client_id}
                className="sub-row"
                style={{
                  ...cardStyle,
                  display: 'flex',
                  alignItems: isMobile ? 'flex-start' : 'center',
                  flexDirection: isMobile ? 'column' : 'row',
                  gap: isMobile ? 12 : 16,
                  padding: '14px 18px',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                }}
                onClick={() => handleOpenDetail(sub)}
              >
                {/* Avatar + Info */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 0, width: isMobile ? '100%' : undefined }}>
                  <div
                    style={{
                      width: 42,
                      height: 42,
                      borderRadius: 12,
                      background: !sub.is_active ? `${colors.text}10` : 'linear-gradient(135deg, #6366F1, #8B5CF6)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 15,
                      fontWeight: 700,
                      color: !sub.is_active ? colors.textSecondary : '#fff',
                      flexShrink: 0,
                    }}
                  >
                    {getInitials(sub.client_name)}
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 14, fontWeight: 600, color: colors.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {sub.client_name || 'Unnamed'}
                      </span>
                      <span
                        style={{
                          fontSize: 10,
                          fontWeight: 600,
                          padding: '2px 8px',
                          borderRadius: 6,
                          background: !sub.is_active ? 'rgba(239,68,68,0.1)' : 'rgba(16,185,129,0.1)',
                          color: !sub.is_active ? '#EF4444' : '#10B981',
                        }}
                      >
                        {!sub.is_active ? 'Disabled' : 'Active'}
                      </span>
                      {/* Shown only when granted — marking the exception rather
                          than the default keeps the org list scannable. */}
                      {Boolean(sub.can_manage_api_keys) && (
                        <Tooltip title="Can manage their own API keys">
                          <span
                            aria-label="Can manage their own API keys"
                            style={{
                              display: 'inline-flex', alignItems: 'center', gap: 4,
                              fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 6,
                              background: 'rgba(99,102,241,0.1)', color: '#6366F1',
                            }}
                          >
                            <Key size={10} /> Keys
                          </span>
                        </Tooltip>
                      )}
                    </div>
                    <div style={{ fontSize: 12, color: colors.textSecondary, marginTop: 2 }}>
                      {sub.client_email}
                    </div>
                  </div>
                </div>

                {/* Balance + Budget */}
                <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 12 : 20, flexWrap: 'wrap', width: isMobile ? '100%' : undefined }}>
                  <div style={{ minWidth: 80 }}>
                    <div style={{ fontSize: 10, color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 500 }}>
                      Balance
                    </div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: colors.text }}>
                      {Number(sub.client_balance || 0).toFixed(2)}
                    </div>
                  </div>
                  {sub.daily_limit != null && (
                    <div style={{ minWidth: 80 }}>
                      <div style={{ fontSize: 10, color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 500 }}>
                        Daily
                      </div>
                      <div style={{ fontSize: 13, fontWeight: 500, color: colors.text }}>
                        {Number(sub.daily_spent || 0).toFixed(2)} / {Number(sub.daily_limit).toFixed(2)}
                      </div>
                    </div>
                  )}

                  {/* Action buttons */}
                  <div style={{ display: 'flex', gap: 6, marginLeft: isMobile ? 0 : 'auto' }}>
                    <Tooltip title="Allocate funds">
                      <button
                        className="sub-icon-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          setAllocateSubId(sub.client_id);
                          setAllocateAmount('');
                          setAllocateModalOpen(true);
                        }}
                        style={{
                          width: 34,
                          height: 34,
                          borderRadius: 8,
                          border: `1px solid ${colors.border}`,
                          background: 'transparent',
                          color: '#10B981',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'all 0.15s ease',
                        }}
                      >
                        <Send size={13} />
                      </button>
                    </Tooltip>
                    <Tooltip title="Recall funds">
                      <button
                        className="sub-icon-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          setRecallSubId(sub.client_id);
                          setRecallAmount('');
                          setRecallModalOpen(true);
                        }}
                        style={{
                          width: 34,
                          height: 34,
                          borderRadius: 8,
                          border: `1px solid ${colors.border}`,
                          background: 'transparent',
                          color: '#F59E0B',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'all 0.15s ease',
                        }}
                      >
                        <ArrowDownLeft size={13} />
                      </button>
                    </Tooltip>
                    <Tooltip title={!sub.is_active ? 'Activate' : 'Deactivate'}>
                      <Popconfirm
                        title={!sub.is_active ? 'Activate this member?' : 'Deactivate this member?'}
                        onConfirm={(e) => {
                          e?.stopPropagation?.();
                          handleToggleActive(sub);
                        }}
                        onCancel={(e) => e?.stopPropagation?.()}
                        okText="Yes"
                        cancelText="No"
                      >
                        <button
                          className="sub-icon-btn"
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            width: 34,
                            height: 34,
                            borderRadius: 8,
                            border: `1px solid ${colors.border}`,
                            background: 'transparent',
                            color: !sub.is_active ? '#10B981' : '#EF4444',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.15s ease',
                          }}
                        >
                          {!sub.is_active ? <Power size={13} /> : <PowerOff size={13} />}
                        </button>
                      </Popconfirm>
                    </Tooltip>
                    <div style={{ width: 34, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center', color: colors.textSecondary }}>
                      <ChevronRight size={16} />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ═══ PENDING INVITATIONS ═══ */}
      {sentInvitations.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: colors.text, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Mail size={15} />
            Sent Invitations
            <span style={{ fontSize: 11, fontWeight: 500, color: colors.textSecondary, background: `${colors.text}08`, padding: '2px 8px', borderRadius: 10 }}>
              {sentInvitations.filter((i) => i.status === 'pending').length} pending
            </span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {sentInvitations.map((inv) => {
              const statusColors = {
                pending: { bg: 'rgba(245,158,11,0.1)', color: '#F59E0B', label: 'Pending' },
                accepted: { bg: 'rgba(34,197,94,0.1)', color: '#22C55E', label: 'Accepted' },
                rejected: { bg: 'rgba(239,68,68,0.1)', color: '#EF4444', label: 'Declined' },
                expired: { bg: 'rgba(156,163,175,0.1)', color: '#9CA3AF', label: 'Expired' },
                cancelled: { bg: 'rgba(156,163,175,0.1)', color: '#9CA3AF', label: 'Cancelled' },
              };
              const sc = statusColors[inv.status] || statusColors.pending;
              return (
                <div
                  key={inv.invitation_id}
                  className="sub-row"
                  style={{
                    ...cardStyle,
                    padding: '12px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 8,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
                    <div style={{ width: 32, height: 32, borderRadius: 8, background: `${colors.text}06`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Mail size={14} style={{ color: colors.textSecondary }} />
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 500, color: colors.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {inv.invitee_name || inv.invitee_email}
                      </div>
                      {inv.invitee_name && (
                        <div style={{ fontSize: 11, color: colors.textSecondary }}>{inv.invitee_email}</div>
                      )}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 6, background: sc.bg, color: sc.color }}>
                      {sc.label}
                    </span>
                    {inv.status === 'pending' && (
                      <Popconfirm title="Cancel this invitation?" onConfirm={() => handleCancelInvitation(inv.invitation_id)} okText="Yes" cancelText="No">
                        <button
                          className="sub-icon-btn"
                          style={{ width: 28, height: 28, borderRadius: 6, border: 'none', background: 'transparent', color: colors.textSecondary, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                          title="Cancel"
                        >
                          <XCircle size={14} />
                        </button>
                      </Popconfirm>
                    )}
                    {(inv.status === 'expired' || inv.status === 'rejected') && (
                      <button
                        className="sub-icon-btn"
                        onClick={() => handleResendInvitation(inv.invitation_id)}
                        style={{ width: 28, height: 28, borderRadius: 6, border: 'none', background: 'transparent', color: colors.textSecondary, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        title="Resend"
                      >
                        <RotateCw size={14} />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ═══ CREATE SUB-ACCOUNT MODAL ═══ */}
      <Modal
        open={createModalOpen}
        title={null}
        onCancel={() => setCreateModalOpen(false)}
        footer={null}
        width={480}
      >
        <div style={{ padding: '8px 0' }}>
          <div style={{ textAlign: 'center', marginBottom: 20 }}>
            <div
              style={{
                width: 44,
                height: 44,
                borderRadius: 12,
                background: 'linear-gradient(135deg, #6366F1, #8B5CF6)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 12px',
              }}
            >
              <Plus size={20} color="#fff" />
            </div>
            <div style={{ fontSize: 17, fontWeight: 600 }}>New Sub-Account</div>
            <p style={{ fontSize: 13, color: colors.textSecondary, marginTop: 4 }}>
              Create a team member with their own balance and limits
            </p>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 500, color: colors.textSecondary, display: 'block', marginBottom: 6 }}>
                Email
              </label>
              <input
                value={createForm.email}
                onChange={(e) => setCreateForm((p) => ({ ...p, email: e.target.value }))}
                placeholder="member@company.com"
                type="email"
                style={inputStyle}
              />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 500, color: colors.textSecondary, display: 'block', marginBottom: 6 }}>
                Display Name
              </label>
              <input
                value={createForm.name}
                onChange={(e) => setCreateForm((p) => ({ ...p, name: e.target.value }))}
                placeholder="John Doe"
                style={inputStyle}
              />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 500, color: colors.textSecondary, display: 'block', marginBottom: 6 }}>
                Password
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showCreatePw ? 'text' : 'password'}
                  value={createForm.password}
                  onChange={(e) => setCreateForm((p) => ({ ...p, password: e.target.value }))}
                  placeholder="At least 6 characters"
                  style={{ ...inputStyle, paddingRight: 40 }}
                />
                <button
                  type="button"
                  onClick={() => setShowCreatePw(!showCreatePw)}
                  style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: colors.textSecondary, cursor: 'pointer', padding: 4 }}
                >
                  {showCreatePw ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </div>

            <div style={{ borderTop: `1px solid ${colors.border}`, paddingTop: 14 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: colors.text, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                <DollarSign size={14} />
                Spending Limits (optional)
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 500, color: colors.textSecondary, display: 'block', marginBottom: 6 }}>
                    Daily Limit
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={createForm.daily_limit}
                    onChange={(e) => setCreateForm((p) => ({ ...p, daily_limit: e.target.value }))}
                    placeholder="No limit"
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 500, color: colors.textSecondary, display: 'block', marginBottom: 6 }}>
                    Monthly Limit
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={createForm.monthly_limit}
                    onChange={(e) => setCreateForm((p) => ({ ...p, monthly_limit: e.target.value }))}
                    placeholder="No limit"
                    style={inputStyle}
                  />
                </div>
              </div>

              {/* Opt-in at creation. Off is the default and the safe state;
                  it can be changed later from the member's API Keys tab. */}
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 12, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={!!createForm.can_manage_api_keys}
                  onChange={(e) => setCreateForm((p) => ({ ...p, can_manage_api_keys: e.target.checked }))}
                  style={{ marginTop: 2, cursor: 'pointer' }}
                />
                <span>
                  <span style={{ fontSize: 13, fontWeight: 500, color: colors.text, display: 'block' }}>
                    Let them manage their own API keys
                  </span>
                  <span style={{ fontSize: 12, color: colors.textSecondary }}>
                    They can create, rename and delete their own keys from the dashboard. You can change this later.
                  </span>
                </span>
              </label>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
            <button
              onClick={() => setCreateModalOpen(false)}
              style={{ padding: '8px 16px', borderRadius: 8, border: `1px solid ${colors.border}`, background: 'transparent', color: colors.textSecondary, cursor: 'pointer', fontSize: 13, fontWeight: 500 }}
            >
              Cancel
            </button>
            <button
              onClick={handleCreate}
              disabled={createLoading}
              style={{
                padding: '8px 20px',
                borderRadius: 8,
                border: 'none',
                background: '#6366F1',
                color: '#fff',
                cursor: createLoading ? 'not-allowed' : 'pointer',
                fontSize: 13,
                fontWeight: 600,
                opacity: createLoading ? 0.6 : 1,
                transition: 'all 0.15s ease',
              }}
            >
              {createLoading ? 'Creating...' : 'Create Member'}
            </button>
          </div>
        </div>
      </Modal>

      {/* ═══ INVITE EXISTING ACCOUNT MODAL ═══ */}
      <Modal
        open={inviteModalOpen}
        title={null}
        onCancel={() => setInviteModalOpen(false)}
        footer={null}
        width={480}
      >
        <div style={{ padding: '8px 0' }}>
          <div style={{ textAlign: 'center', marginBottom: 20 }}>
            <div
              style={{
                width: 44,
                height: 44,
                borderRadius: 12,
                background: 'linear-gradient(135deg, #6366F1, #8B5CF6)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 12px',
              }}
            >
              <Mail size={20} color="#fff" />
            </div>
            <div style={{ fontSize: 17, fontWeight: 600 }}>Invite Existing Account</div>
            <p style={{ fontSize: 13, color: colors.textSecondary, marginTop: 4 }}>
              Invite an existing FluxUro account to join as a sub-account. They'll keep their balance and API keys.
            </p>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 500, color: colors.textSecondary, display: 'block', marginBottom: 6 }}>
                Email Address
              </label>
              <input
                value={inviteForm.email}
                onChange={(e) => setInviteForm((p) => ({ ...p, email: e.target.value }))}
                placeholder="Enter existing account email"
                type="email"
                style={inputStyle}
              />
            </div>

            <div style={{ borderTop: `1px solid ${colors.border}`, paddingTop: 14 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: colors.text, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                <DollarSign size={14} />
                Spending Limits (optional)
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 500, color: colors.textSecondary, display: 'block', marginBottom: 6 }}>
                    Daily Limit
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={inviteForm.daily_limit}
                    onChange={(e) => setInviteForm((p) => ({ ...p, daily_limit: e.target.value }))}
                    placeholder="No limit"
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 500, color: colors.textSecondary, display: 'block', marginBottom: 6 }}>
                    Monthly Limit
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={inviteForm.monthly_limit}
                    onChange={(e) => setInviteForm((p) => ({ ...p, monthly_limit: e.target.value }))}
                    placeholder="No limit"
                    style={inputStyle}
                  />
                </div>
              </div>
            </div>

            <div style={{ padding: '10px 12px', borderRadius: 8, background: 'rgba(99,102,241,0.06)', border: '1px solid rgba(99,102,241,0.12)', fontSize: 12, color: colors.textSecondary }}>
              The invited account will receive an email with a link to accept. The invitation expires in 1 hour.
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
            <button
              onClick={() => setInviteModalOpen(false)}
              style={{ padding: '8px 16px', borderRadius: 8, border: `1px solid ${colors.border}`, background: 'transparent', color: colors.textSecondary, cursor: 'pointer', fontSize: 13, fontWeight: 500 }}
            >
              Cancel
            </button>
            <button
              onClick={handleInvite}
              disabled={inviteLoading}
              style={{
                padding: '8px 20px',
                borderRadius: 8,
                border: 'none',
                background: '#6366F1',
                color: '#fff',
                cursor: inviteLoading ? 'not-allowed' : 'pointer',
                fontSize: 13,
                fontWeight: 600,
                opacity: inviteLoading ? 0.6 : 1,
                transition: 'all 0.15s ease',
              }}
            >
              {inviteLoading ? 'Sending...' : 'Send Invitation'}
            </button>
          </div>
        </div>
      </Modal>

      {/* ═══ DETAIL MODAL ═══ */}
      <Modal
        open={detailModalOpen}
        title={null}
        onCancel={() => setDetailModalOpen(false)}
        footer={null}
        width={560}
      >
        {selectedSub && (
          <div style={{ padding: '8px 0' }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
              <div
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: 14,
                  background: 'linear-gradient(135deg, #6366F1, #8B5CF6)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 18,
                  fontWeight: 700,
                  color: '#fff',
                }}
              >
                {getInitials(selectedSub.client_name)}
              </div>
              <div>
                <div style={{ fontSize: 17, fontWeight: 600, color: colors.text }}>{selectedSub.client_name}</div>
                <div style={{ fontSize: 13, color: colors.textSecondary }}>{selectedSub.client_email}</div>
              </div>
              <span
                style={{
                  marginLeft: 'auto',
                  fontSize: 11,
                  fontWeight: 600,
                  padding: '3px 10px',
                  borderRadius: 6,
                  background: !selectedSub.is_active ? 'rgba(239,68,68,0.1)' : 'rgba(16,185,129,0.1)',
                  color: !selectedSub.is_active ? '#EF4444' : '#10B981',
                }}
              >
                {!selectedSub.is_active ? 'Disabled' : 'Active'}
              </span>
            </div>

            {/* Tabs */}
            <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: `1px solid ${colors.border}`, paddingBottom: 0 }}>
              {[
                { key: 'overview', label: 'Overview', icon: <User size={13} /> },
                { key: 'budget', label: 'Budget', icon: <DollarSign size={13} /> },
                { key: 'apikeys', label: 'API Keys', icon: <Key size={13} /> },
                { key: 'usage', label: 'Usage Access', icon: <Eye size={13} /> },
                { key: 'password', label: 'Password', icon: <Lock size={13} /> },
              ].map((t) => (
                <button
                  key={t.key}
                  onClick={() => {
                    setDetailTab(t.key);
                    if (t.key === 'apikeys') fetchSubApiKeys(selectedSub.client_id);
                    // Only load once per sub-account — refetching on every tab
                    // click would throw away unsaved selections.
                    if (t.key === 'usage' && usageAccessFor !== selectedSub.client_id) {
                      fetchUsageAccess(selectedSub.client_id);
                    }
                  }}
                  style={{
                    padding: '8px 14px',
                    border: 'none',
                    borderBottom: `2px solid ${detailTab === t.key ? '#6366F1' : 'transparent'}`,
                    background: 'transparent',
                    color: detailTab === t.key ? '#6366F1' : colors.textSecondary,
                    fontSize: 13,
                    fontWeight: detailTab === t.key ? 600 : 500,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    transition: 'all 0.15s ease',
                    marginBottom: -1,
                  }}
                >
                  {t.icon}
                  {!isMobile && t.label}
                </button>
              ))}
            </div>

            {/* Tab content */}
            {detailTab === 'overview' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {/* Balance card */}
                <div style={{ padding: 16, borderRadius: 12, background: `${colors.text}04`, border: `1px solid ${colors.border}` }}>
                  <div style={{ fontSize: 11, color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 500, marginBottom: 6 }}>
                    Current Balance
                  </div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                    <span style={{ fontSize: 28, fontWeight: 700, color: colors.text }}>{Number(selectedSub.client_balance || 0).toFixed(2)}</span>
                    <span style={{ fontSize: 14, color: colors.textSecondary, fontWeight: 500 }}>URO</span>
                  </div>
                  {selectedSub.client_frozen_balance > 0 && (
                    <div style={{ fontSize: 12, color: colors.textSecondary, marginTop: 4 }}>
                      Frozen: {Number(selectedSub.client_frozen_balance).toFixed(2)} URO
                    </div>
                  )}
                </div>

                {/* Quick actions */}
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    onClick={() => {
                      setAllocateSubId(selectedSub.client_id);
                      setAllocateAmount('');
                      setAllocateModalOpen(true);
                    }}
                    className="sub-action-btn"
                    style={{
                      flex: 1,
                      height: 40,
                      borderRadius: 10,
                      border: `1px solid rgba(16,185,129,0.3)`,
                      background: 'rgba(16,185,129,0.06)',
                      color: '#10B981',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 6,
                      fontSize: 13,
                      fontWeight: 600,
                      transition: 'all 0.15s ease',
                    }}
                  >
                    <Send size={13} />
                    Allocate Funds
                  </button>
                  <button
                    onClick={() => {
                      setRecallSubId(selectedSub.client_id);
                      setRecallAmount('');
                      setRecallModalOpen(true);
                    }}
                    className="sub-action-btn"
                    style={{
                      flex: 1,
                      height: 40,
                      borderRadius: 10,
                      border: `1px solid rgba(245,158,11,0.3)`,
                      background: 'rgba(245,158,11,0.06)',
                      color: '#F59E0B',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 6,
                      fontSize: 13,
                      fontWeight: 600,
                      transition: 'all 0.15s ease',
                    }}
                  >
                    <ArrowDownLeft size={13} />
                    Recall Funds
                  </button>
                </div>

                {/* Spending info */}
                {(selectedSub.daily_limit != null || selectedSub.monthly_limit != null) && (
                  <div style={{ padding: 16, borderRadius: 12, background: `${colors.text}04`, border: `1px solid ${colors.border}` }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: colors.text, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                      <TrendingUp size={14} />
                      Spending
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                      {selectedSub.daily_limit != null && (
                        <div>
                          <div style={{ fontSize: 11, color: colors.textSecondary, marginBottom: 4 }}>Daily</div>
                          <div style={{ fontSize: 14, fontWeight: 600, color: colors.text }}>
                            {Number(selectedSub.daily_spent || 0).toFixed(2)} / {Number(selectedSub.daily_limit).toFixed(2)}
                          </div>
                          <div style={{ marginTop: 6, height: 4, borderRadius: 2, background: `${colors.text}10`, overflow: 'hidden' }}>
                            <div
                              style={{
                                height: '100%',
                                borderRadius: 2,
                                background: Number(selectedSub.daily_spent || 0) >= Number(selectedSub.daily_limit) ? '#EF4444' : '#6366F1',
                                width: `${Math.min(100, (Number(selectedSub.daily_spent || 0) / Number(selectedSub.daily_limit)) * 100)}%`,
                                transition: 'width 0.3s ease',
                              }}
                            />
                          </div>
                        </div>
                      )}
                      {selectedSub.monthly_limit != null && (
                        <div>
                          <div style={{ fontSize: 11, color: colors.textSecondary, marginBottom: 4 }}>Monthly</div>
                          <div style={{ fontSize: 14, fontWeight: 600, color: colors.text }}>
                            {Number(selectedSub.monthly_spent || 0).toFixed(2)} / {Number(selectedSub.monthly_limit).toFixed(2)}
                          </div>
                          <div style={{ marginTop: 6, height: 4, borderRadius: 2, background: `${colors.text}10`, overflow: 'hidden' }}>
                            <div
                              style={{
                                height: '100%',
                                borderRadius: 2,
                                background: Number(selectedSub.monthly_spent || 0) >= Number(selectedSub.monthly_limit) ? '#EF4444' : '#10B981',
                                width: `${Math.min(100, (Number(selectedSub.monthly_spent || 0) / Number(selectedSub.monthly_limit)) * 100)}%`,
                                transition: 'width 0.3s ease',
                              }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {detailTab === 'budget' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div style={{ padding: '10px 12px', borderRadius: 8, background: `${colors.text}04`, border: `1px solid ${colors.border}`, fontSize: 12, color: colors.textSecondary }}>
                  Set daily and monthly spending limits. Leave blank for unlimited. Limits reset automatically.
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 500, color: colors.textSecondary, display: 'block', marginBottom: 6 }}>
                      Daily Limit (URO)
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={budgetForm.daily_limit}
                      onChange={(e) => setBudgetForm((p) => ({ ...p, daily_limit: e.target.value }))}
                      placeholder="Unlimited"
                      style={inputStyle}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 500, color: colors.textSecondary, display: 'block', marginBottom: 6 }}>
                      Monthly Limit (URO)
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={budgetForm.monthly_limit}
                      onChange={(e) => setBudgetForm((p) => ({ ...p, monthly_limit: e.target.value }))}
                      placeholder="Unlimited"
                      style={inputStyle}
                    />
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button
                    onClick={handleSaveBudget}
                    disabled={budgetSaving}
                    style={{
                      padding: '8px 20px',
                      borderRadius: 8,
                      border: 'none',
                      background: '#6366F1',
                      color: '#fff',
                      cursor: budgetSaving ? 'not-allowed' : 'pointer',
                      fontSize: 13,
                      fontWeight: 600,
                      opacity: budgetSaving ? 0.6 : 1,
                    }}
                  >
                    {budgetSaving ? 'Saving...' : 'Save Budget'}
                  </button>
                </div>
              </div>
            )}

            {detailTab === 'apikeys' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {/* Permission — the rule sits directly above the list it governs,
                    so the keys below read as its visible consequence. */}
                <div style={{
                  padding: '12px 14px', borderRadius: 10,
                  background: subCanManageKeys ? 'rgba(99,102,241,0.06)' : `${colors.text}04`,
                  border: `1px solid ${subCanManageKeys ? 'rgba(99,102,241,0.12)' : colors.border}`,
                  transition: 'background .15s ease, border-color .15s ease',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ flex: 1, fontSize: 13, fontWeight: 600, color: colors.text }}>
                      Manage their own API keys
                    </div>
                    <Popconfirm
                      title={subCanManageKeys
                        ? `Stop ${subFirstName} from managing API keys?`
                        : `Let ${subFirstName} manage these API keys?`}
                      description={
                        <div style={{ maxWidth: 300, fontSize: 12, lineHeight: 1.5 }}>
                          {subCanManageKeys
                            ? 'Any key they already created stays active — turning this off does not disable it. Remove the ones you don’t want left running.'
                            : 'They’ll be able to create new keys, and rename or delete any key on this list — including the ones you created for them. Only while signed in to the dashboard; their API keys can’t mint more keys. You can turn this off at any time.'}
                        </div>
                      }
                      okText={subCanManageKeys ? 'Turn off' : 'Allow'}
                      cancelText="Cancel"
                      // antd's Popconfirm does not autofocus, and the popup is a
                      // body portal — without this a keyboard user opens the
                      // confirm and then Tabs through the modal behind it.
                      okButtonProps={{ autoFocus: true }}
                      onConfirm={() => handleToggleKeyPermission(!subCanManageKeys)}
                    >
                      {/* Wrapper widens the 44x22 switch to a 56x34 hit area
                          (WCAG 2.2 target-minimum) with no layout shift, and
                          gives Popconfirm a plain element to bind onClick to.
                          The Switch has NO onChange on purpose: the thumb must
                          not move until the server confirms. */}
                      <span
                        aria-haspopup="dialog"
                        style={{
                          display: 'inline-flex', padding: 6, margin: -6, cursor: 'pointer',
                          // antd disables the switch itself while loading, but the
                          // padded hit area would still open the confirm mid-save.
                          pointerEvents: keyPermSaving ? 'none' : undefined,
                        }}
                      >
                        <Switch checked={subCanManageKeys} loading={keyPermSaving} />
                      </span>
                    </Popconfirm>
                  </div>
                  <div style={{ marginTop: 6, fontSize: 12, lineHeight: 1.5, color: colors.textSecondary }}>
                    {subCanManageKeys
                      ? `${subFirstName} can create, rename and delete the keys below from their own Account → API keys page — including keys you created for them.`
                      : `${subFirstName} can see the keys below, but only you can create, rename or delete them. This is the default.`}
                  </div>
                </div>

                {/* Revoking is not destructive, it is INCOMPLETE — the keys it
                    leaves behind are the risk. Count is bound live, so this
                    counts down as keys are removed and disappears at zero. */}
                {keysRevokedNotice && subSelfMintedKeys.length > 0 && (
                  <div style={{
                    display: 'flex', gap: 8, padding: '10px 12px', borderRadius: 10,
                    background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.2)',
                    fontSize: 12, lineHeight: 1.5, color: colors.textSecondary,
                  }}>
                    <AlertTriangle size={14} style={{ color: '#F59E0B', flexShrink: 0, marginTop: 1 }} />
                    <span>
                      {subFirstName} can no longer create or delete keys — but the{' '}
                      {subSelfMintedKeys.length} key{subSelfMintedKeys.length === 1 ? '' : 's'} they
                      created stay active. Remove any you don’t want left running.
                    </span>
                  </div>
                )}

                {/* Create */}
                <div style={{ display: 'flex', gap: 8, padding: 12, borderRadius: 10, background: `${colors.text}04`, border: `1px dashed ${colors.border}` }}>
                  <Input
                    placeholder="API key name..."
                    value={newSubKeyName}
                    onChange={(e) => setNewSubKeyName(e.target.value)}
                    onPressEnter={handleCreateSubApiKey}
                    style={{ background: 'transparent', border: `1px solid ${colors.border}`, borderRadius: 8 }}
                  />
                  <Button
                    type="primary"
                    loading={creatingSubKey}
                    onClick={handleCreateSubApiKey}
                    disabled={!newSubKeyName?.trim()}
                    style={{ borderRadius: 8, background: newSubKeyName?.trim() ? '#6366F1' : undefined, borderColor: newSubKeyName?.trim() ? '#6366F1' : undefined }}
                    icon={<Plus size={14} />}
                  >
                    Create
                  </Button>
                </div>

                {/* List */}
                {subApiKeysLoading ? (
                  <div style={{ textAlign: 'center', padding: 20, color: colors.textSecondary, fontSize: 13 }}>Loading...</div>
                ) : subApiKeys.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '24px 16px', color: colors.textSecondary, border: `1px dashed ${colors.border}`, borderRadius: 10 }}>
                    <Key size={20} style={{ marginBottom: 8, opacity: 0.4 }} />
                    <div style={{ fontSize: 13 }}>No API keys yet</div>
                    <div style={{ fontSize: 12, marginTop: 3, opacity: 0.75 }}>
                      {subCanManageKeys
                        ? `Create one above, or ${subFirstName} can create their own.`
                        : 'Create one above to get started.'}
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {subApiKeys.map((k) => (
                      <div
                        key={k.client_api_key_id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                          padding: '10px 12px',
                          borderRadius: 8,
                          border: `1px solid ${colors.border}`,
                        }}
                      >
                        <Key size={14} style={{ color: colors.textSecondary, flexShrink: 0 }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 500, color: colors.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {k.api_key_name}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            {k.api_key_prefix && (
                              <span style={{ fontSize: 11, fontFamily: 'monospace', color: colors.textSecondary }}>{k.api_key_prefix}{k.key_preview && k.key_preview !== '...' ? k.key_preview : '...'}</span>
                            )}
                            {/* Provenance. NULL means the key predates attribution —
                                render nothing rather than guessing, because a wrong
                                attribution in a security list reads as evidence. */}
                            {k.created_by_client_id && (
                              <span style={{
                                fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 5,
                                background: k.created_by_client_id === selectedSub?.client_id
                                  ? 'rgba(99,102,241,0.1)' : `${colors.text}08`,
                                color: k.created_by_client_id === selectedSub?.client_id
                                  ? '#6366F1' : colors.textSecondary,
                              }}>
                                {k.created_by_client_id === selectedSub?.client_id
                                  ? `Created by ${subFirstName}`
                                  : 'Created by you'}
                              </span>
                            )}
                          </div>
                        </div>
                        <Popconfirm title="Remove this API key?" onConfirm={() => handleDeleteSubApiKey(k.client_api_key_id)} okText="Yes" cancelText="No">
                          <button style={{ width: 30, height: 30, borderRadius: 6, border: 'none', background: 'transparent', color: colors.textSecondary, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Trash2 size={13} />
                          </button>
                        </Popconfirm>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {detailTab === 'usage' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {/* Mode — one decision, everything below is its consequence */}
                <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 4, padding: 4, borderRadius: 12, background: `${colors.text}04`, border: `1px solid ${colors.border}` }}>
                  {[
                    { key: 'own', label: 'Own usage only' },
                    { key: 'selected', label: 'Selected' },
                    { key: 'all', label: 'Entire organization' },
                  ].map((opt) => {
                    const active = usageScope === opt.key;
                    return (
                      <button
                        key={opt.key}
                        onClick={() => setUsageScope(opt.key)}
                        aria-pressed={active}
                        style={{
                          flex: 1, height: isMobile ? 40 : 36, borderRadius: 8, border: 'none',
                          cursor: 'pointer', fontSize: isMobile ? 13 : 12.5,
                          fontWeight: active ? 600 : 500,
                          background: active ? '#6366F1' : 'transparent',
                          color: active ? '#fff' : colors.textSecondary,
                          boxShadow: active ? '0 1px 3px rgba(99,102,241,0.35)' : 'none',
                          transition: 'all 0.15s ease',
                        }}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>

                {/* Load failure is rendered OUTSIDE the 'selected' branch: the
                    error path no longer forces the mode to 'own', so nesting the
                    retry under 'selected' would leave it unreachable. */}
                {usageError && (
                  <div style={{ textAlign: 'center', padding: '20px 16px', color: colors.textSecondary, border: `1px dashed ${colors.border}`, borderRadius: 10 }}>
                    <AlertTriangle size={20} color="#F59E0B" style={{ marginBottom: 8 }} />
                    <div style={{ fontSize: 13, marginBottom: 4 }}>Couldn't load usage access</div>
                    <div style={{ fontSize: 11.5, marginBottom: 10 }}>Saving is disabled until this loads.</div>
                    <Button size="small" onClick={() => fetchUsageAccess(selectedSub.client_id)} icon={<RotateCw size={12} />} style={{ borderRadius: 8 }}>
                      Retry
                    </Button>
                  </div>
                )}

                {/* Plain-English consequence for THIS member */}
                {!usageError && (usageScope === 'all' ? (
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '10px 12px', borderRadius: 8, background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.15)' }}>
                    <AlertTriangle size={14} color="#F59E0B" style={{ flexShrink: 0, marginTop: 1 }} />
                    <span style={{ fontSize: 12, color: '#F59E0B', fontWeight: 500, lineHeight: 1.5 }}>
                      {firstName(selectedSub.client_name)} will see usage for every account and key in your
                      organization — including sub-accounts and keys you add later.
                    </span>
                  </div>
                ) : (
                  <div style={{ padding: '10px 12px', borderRadius: 8, background: `${colors.text}04`, border: `1px solid ${colors.border}`, fontSize: 12, lineHeight: 1.5, color: colors.textSecondary }}>
                    {usageScope === 'own'
                      ? `${firstName(selectedSub.client_name)} sees only usage from the API keys they own. This is the default.`
                      : `${firstName(selectedSub.client_name)} sees their own usage, plus the accounts and keys you pick below.`}
                  </div>
                ))}

                {usageScope === 'selected' && !usageError && (
                  usageLoading ? (
                    <div style={{ textAlign: 'center', padding: 24, color: colors.textSecondary, fontSize: 13 }}>Loading...</div>
                  ) : !usageInventory ? (
                    <div style={{ textAlign: 'center', padding: '24px 16px', color: colors.textSecondary, border: `1px dashed ${colors.border}`, borderRadius: 10, fontSize: 13 }}>
                      No accounts loaded yet.
                    </div>
                  ) : (
                    <>
                      {usageTotalKeys > 10 && (
                        <Input
                          allowClear
                          prefix={<Search size={13} style={{ color: colors.textSecondary }} />}
                          placeholder="Search accounts and keys..."
                          value={usageSearch}
                          onChange={(e) => setUsageSearch(e.target.value)}
                          style={{ background: 'transparent', border: `1px solid ${colors.border}`, borderRadius: 8 }}
                        />
                      )}

                      <div style={{ maxHeight: isMobile ? 300 : 340, overflowY: 'auto', border: `1px solid ${colors.border}`, borderRadius: 12 }}>
                        {usageVisibleAccounts.length === 0 ? (
                          <div style={{ padding: '24px 16px', textAlign: 'center', color: colors.textSecondary, fontSize: 13 }}>
                            No accounts or keys match "{usageSearch}"
                          </div>
                        ) : usageVisibleAccounts.map((acct, idx) => {
                          // The member being edited always sees itself; rendering it
                          // as revocable would be a lie.
                          const isSelf = acct.client_id === selectedSub.client_id;
                          const accountGranted = usageAccounts.includes(acct.client_id);
                          const keys = acct.visibleKeys;
                          // Counted over the FULL key list, never the search-filtered
                          // one: an account with granted keys that don't match the
                          // query must still render its indeterminate dash, or it
                          // reads as "nothing shared here" and one click on the row
                          // grants the whole account.
                          const picked = (acct.api_keys || []).filter((k) => usageKeys.includes(k.client_api_key_id)).length;
                          // A key-only search match auto-expands that group so the
                          // hit is visible without another click — but an explicit
                          // toggle still wins, otherwise the chevron is a no-op
                          // while a search is active (`||` would override the
                          // stored `false` every render).
                          const expanded = expandedAccounts[acct.client_id] ?? Boolean(usageSearch && acct.keyMatched);
                          const indeterminate = !accountGranted && picked > 0;

                          return (
                            <div key={acct.client_id} style={{ borderBottom: idx === usageVisibleAccounts.length - 1 ? 'none' : `1px solid ${colors.border}` }}>
                              <div
                                onClick={() => { if (!isSelf) setUsageAccounts((p) => toggleIn(p, acct.client_id)); }}
                                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', minHeight: 52, cursor: isSelf ? 'default' : 'pointer', opacity: isSelf ? 0.85 : 1 }}
                              >
                                <span style={{
                                  width: 18, height: 18, borderRadius: 5, flexShrink: 0,
                                  border: `1.5px solid ${(accountGranted || isSelf || indeterminate) ? '#6366F1' : colors.border}`,
                                  background: (accountGranted || isSelf) ? '#6366F1' : 'transparent',
                                  opacity: isSelf ? 0.45 : 1,
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}>
                                  {(accountGranted || isSelf) && <Check size={12} color="#fff" strokeWidth={3} />}
                                  {indeterminate && <Minus size={12} color="#6366F1" strokeWidth={3} />}
                                </span>
                                <div style={{ width: 28, height: 28, borderRadius: 8, flexShrink: 0, background: 'linear-gradient(135deg, #6366F1, #8B5CF6)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: '#fff' }}>
                                  {getInitials(acct.client_name)}
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ fontSize: 13, fontWeight: 600, color: colors.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {acct.client_name || acct.client_email}
                                    {acct.isMain && <span style={{ fontSize: 10, fontWeight: 600, color: '#6366F1', background: 'rgba(99,102,241,0.1)', padding: '2px 8px', borderRadius: 6, marginLeft: 6 }}>Main account</span>}
                                    {isSelf && <span style={{ fontSize: 10, fontWeight: 600, color: '#6366F1', background: 'rgba(99,102,241,0.1)', padding: '2px 8px', borderRadius: 6, marginLeft: 6 }}>This member</span>}
                                  </div>
                                  <div style={{ fontSize: 11, color: colors.textSecondary, marginTop: 1 }}>
                                    {isSelf ? 'Always included' : accountGranted ? 'Entire account shared' : indeterminate ? <span style={{ color: '#6366F1', fontWeight: 600 }}>{picked} of {acct.api_keys.length} keys</span> : `${acct.api_keys.length} key${acct.api_keys.length === 1 ? '' : 's'}`}
                                  </div>
                                </div>
                                {!isSelf && acct.api_keys.length > 0 && (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); setExpandedAccounts((p) => ({ ...p, [acct.client_id]: !expanded })); }}
                                    style={{ background: 'transparent', border: 'none', color: colors.textSecondary, cursor: 'pointer', fontSize: 11, display: 'flex', alignItems: 'center', gap: 3, padding: '6px 4px', flexShrink: 0 }}
                                  >
                                    Keys
                                    <ChevronRight size={12} style={{ transform: expanded ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }} />
                                  </button>
                                )}
                              </div>

                              {expanded && !isSelf && (
                                <div style={{ borderTop: `1px solid ${colors.border}`, background: accountGranted ? 'rgba(99,102,241,0.03)' : 'transparent', borderLeft: accountGranted ? '2px solid rgba(99,102,241,0.35)' : 'none' }}>
                                  {accountGranted && (
                                    <div style={{ fontSize: 11, color: colors.textSecondary, padding: '6px 12px 6px 40px' }}>
                                      Individual keys are paused while the whole account is shared.
                                    </div>
                                  )}
                                  {keys.length === 0 ? (
                                    <div style={{ fontSize: 12, color: colors.textSecondary, padding: '10px 12px 10px 40px' }}>
                                      {acct.api_keys.length === 0 ? 'No API keys in this account yet.' : 'No keys match your search.'}
                                    </div>
                                  ) : keys.map((k) => {
                                    // Checked-but-dimmed reads as "already covered".
                                    // Greying it out unchecked would read as "unavailable".
                                    const covered = accountGranted;
                                    const checked = covered || usageKeys.includes(k.client_api_key_id);
                                    return (
                                      <div
                                        key={k.client_api_key_id}
                                        onClick={() => { if (!covered) setUsageKeys((p) => toggleIn(p, k.client_api_key_id)); }}
                                        title={covered ? 'Covered by the account-wide grant. Uncheck the account to pick individual keys.' : (k.description || undefined)}
                                        style={{ display: 'flex', alignItems: 'center', gap: 10, height: isMobile ? 44 : 34, padding: '0 12px 0 40px', cursor: covered ? 'not-allowed' : 'pointer', opacity: covered ? 0.55 : 1 }}
                                      >
                                        <span style={{
                                          width: 16, height: 16, borderRadius: 4, flexShrink: 0,
                                          border: `1.5px solid ${checked ? '#6366F1' : colors.border}`,
                                          background: checked ? '#6366F1' : 'transparent',
                                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        }}>
                                          {checked && <Check size={11} color="#fff" strokeWidth={3} />}
                                        </span>
                                        <Key size={13} style={{ color: colors.textSecondary, flexShrink: 0 }} />
                                        <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, color: colors.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                          {k.api_key_name || 'Unnamed key'}
                                        </span>
                                        {k.api_key_prefix && (
                                          <span style={{ fontSize: 11, fontFamily: 'monospace', color: colors.textSecondary, flexShrink: 0 }}>
                                            {k.api_key_prefix}{k.key_preview && k.key_preview !== '...' ? k.key_preview : '...'}
                                          </span>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </>
                  )
                )}

                {/* Running summary, next to Save so it is read at commit time */}
                {usageScope === 'selected' && !usageLoading && !usageError && usageInventory && (
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', borderRadius: 10,
                    background: nothingSelected ? 'rgba(245,158,11,0.06)' : 'rgba(99,102,241,0.06)',
                    border: `1px solid ${nothingSelected ? 'rgba(245,158,11,0.15)' : 'rgba(99,102,241,0.12)'}`,
                  }}>
                    {nothingSelected ? <AlertTriangle size={13} color="#F59E0B" style={{ flexShrink: 0 }} /> : <Eye size={13} color="#6366F1" style={{ flexShrink: 0 }} />}
                    <span style={{ fontSize: 12, color: nothingSelected ? '#F59E0B' : colors.textSecondary, flex: 1 }}>
                      {nothingSelected ? (
                        'Nothing selected — pick an account or key, or switch to Own usage only.'
                      ) : (
                        <>Sharing {usageAccounts.length > 0 && <strong style={{ color: colors.text, fontWeight: 600 }}>{usageAccounts.length} account{usageAccounts.length === 1 ? '' : 's'}</strong>}
                        {usageAccounts.length > 0 && effectiveKeyGrants.length > 0 && ' and '}
                        {effectiveKeyGrants.length > 0 && <strong style={{ color: colors.text, fontWeight: 600 }}>{effectiveKeyGrants.length} key{effectiveKeyGrants.length === 1 ? '' : 's'}</strong>}
                        {' '}— plus their own usage.</>
                      )}
                    </span>
                    {!nothingSelected && (
                      <button
                        onClick={() => { setUsageAccounts([]); setUsageKeys([]); }}
                        style={{ background: 'transparent', border: 'none', color: '#6366F1', fontSize: 11.5, cursor: 'pointer', flexShrink: 0 }}
                      >
                        Clear all
                      </button>
                    )}
                  </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 11.5, color: '#F59E0B', display: 'flex', alignItems: 'center', gap: 6 }}>
                    {usageDirty && !usageSaveBlocked && (<><span style={{ width: 6, height: 6, borderRadius: '50%', background: '#F59E0B' }} />Unsaved changes</>)}
                  </span>
                  <Popconfirm
                    title={`Remove all shared visibility from ${firstName(selectedSub.client_name)}?`}
                    okText="Remove"
                    cancelText="Cancel"
                    // Only gate the destructive direction. Save is a full replace,
                    // so switching to "Own usage only" silently deletes every grant.
                    disabled={usageScope !== 'own' || usageBaseline === 'own'}
                    onConfirm={handleSaveUsageAccess}
                  >
                    <Button
                      type="primary"
                      loading={usageSaving}
                      disabled={usageSaveBlocked}
                      onClick={(usageScope !== 'own' || usageBaseline === 'own') ? handleSaveUsageAccess : undefined}
                      style={{ borderRadius: 8, background: usageSaveBlocked ? undefined : '#6366F1', borderColor: usageSaveBlocked ? undefined : '#6366F1' }}
                    >
                      {usageSaving ? 'Saving...' : 'Save access'}
                    </Button>
                  </Popconfirm>
                </div>
              </div>
            )}

            {detailTab === 'password' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div style={{ padding: '10px 12px', borderRadius: 8, background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.15)', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <AlertTriangle size={14} color="#F59E0B" />
                  <span style={{ fontSize: 12, color: '#F59E0B', fontWeight: 500 }}>
                    This will immediately change the member's password.
                  </span>
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 500, color: colors.textSecondary, display: 'block', marginBottom: 6 }}>
                    New Password
                  </label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showNewPw ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="At least 6 characters"
                      style={{ ...inputStyle, paddingRight: 40 }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPw(!showNewPw)}
                      style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: colors.textSecondary, cursor: 'pointer', padding: 4 }}
                    >
                      {showNewPw ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button
                    onClick={handleResetPassword}
                    disabled={pwSaving || !newPassword || newPassword.length < 6}
                    style={{
                      padding: '8px 20px',
                      borderRadius: 8,
                      border: 'none',
                      background: '#F59E0B',
                      color: '#fff',
                      cursor: (pwSaving || !newPassword || newPassword.length < 6) ? 'not-allowed' : 'pointer',
                      fontSize: 13,
                      fontWeight: 600,
                      opacity: (pwSaving || !newPassword || newPassword.length < 6) ? 0.6 : 1,
                    }}
                  >
                    {pwSaving ? 'Resetting...' : 'Reset Password'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* ═══ ALLOCATE FUNDS MODAL ═══ */}
      <Modal
        open={allocateModalOpen}
        title={null}
        onCancel={() => setAllocateModalOpen(false)}
        footer={null}
        width={400}
      >
        <div style={{ padding: '8px 0', textAlign: 'center' }}>
          <div style={{ width: 44, height: 44, borderRadius: 12, background: 'linear-gradient(135deg, #10B981, #059669)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
            <Send size={20} color="#fff" />
          </div>
          <div style={{ fontSize: 17, fontWeight: 600, marginBottom: 4 }}>Allocate Funds</div>
          <p style={{ fontSize: 13, color: colors.textSecondary, marginBottom: 20 }}>
            Transfer credits from your balance to this member
          </p>
          <div style={{ marginBottom: 16, textAlign: 'left' }}>
            <label style={{ fontSize: 12, fontWeight: 500, color: colors.textSecondary, display: 'block', marginBottom: 6 }}>
              Amount (URO)
            </label>
            <input
              type="number"
              min="0.01"
              step="0.01"
              value={allocateAmount}
              onChange={(e) => setAllocateAmount(e.target.value)}
              placeholder="0.00"
              style={{ ...inputStyle, textAlign: 'center', fontSize: 20, fontWeight: 600 }}
            />
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
            <button
              onClick={() => setAllocateModalOpen(false)}
              style={{ flex: 1, height: 40, borderRadius: 10, border: `1px solid ${colors.border}`, background: 'transparent', color: colors.text, cursor: 'pointer', fontSize: 14, fontWeight: 500 }}
            >
              Cancel
            </button>
            <button
              onClick={handleAllocate}
              disabled={allocateLoading || !allocateAmount || Number(allocateAmount) <= 0}
              style={{
                flex: 1,
                height: 40,
                borderRadius: 10,
                border: 'none',
                background: (allocateLoading || !allocateAmount || Number(allocateAmount) <= 0) ? colors.border : 'linear-gradient(135deg, #10B981, #059669)',
                color: '#fff',
                cursor: (allocateLoading || !allocateAmount || Number(allocateAmount) <= 0) ? 'not-allowed' : 'pointer',
                fontSize: 14,
                fontWeight: 600,
                transition: 'all 0.15s ease',
              }}
            >
              {allocateLoading ? 'Sending...' : 'Allocate'}
            </button>
          </div>
        </div>
      </Modal>

      {/* ═══ RECALL FUNDS MODAL ═══ */}
      <Modal
        open={recallModalOpen}
        title={null}
        onCancel={() => setRecallModalOpen(false)}
        footer={null}
        width={400}
      >
        <div style={{ padding: '8px 0', textAlign: 'center' }}>
          <div style={{ width: 44, height: 44, borderRadius: 12, background: 'linear-gradient(135deg, #F59E0B, #D97706)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
            <ArrowDownLeft size={20} color="#fff" />
          </div>
          <div style={{ fontSize: 17, fontWeight: 600, marginBottom: 4 }}>Recall Funds</div>
          <p style={{ fontSize: 13, color: colors.textSecondary, marginBottom: 20 }}>
            Pull credits back from this member to your balance
          </p>
          <div style={{ marginBottom: 16, textAlign: 'left' }}>
            <label style={{ fontSize: 12, fontWeight: 500, color: colors.textSecondary, display: 'block', marginBottom: 6 }}>
              Amount (URO)
            </label>
            <input
              type="number"
              min="0.01"
              step="0.01"
              value={recallAmount}
              onChange={(e) => setRecallAmount(e.target.value)}
              placeholder="0.00"
              style={{ ...inputStyle, textAlign: 'center', fontSize: 20, fontWeight: 600 }}
            />
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
            <button
              onClick={() => setRecallModalOpen(false)}
              style={{ flex: 1, height: 40, borderRadius: 10, border: `1px solid ${colors.border}`, background: 'transparent', color: colors.text, cursor: 'pointer', fontSize: 14, fontWeight: 500 }}
            >
              Cancel
            </button>
            <button
              onClick={handleRecall}
              disabled={recallLoading || !recallAmount || Number(recallAmount) <= 0}
              style={{
                flex: 1,
                height: 40,
                borderRadius: 10,
                border: 'none',
                background: (recallLoading || !recallAmount || Number(recallAmount) <= 0) ? colors.border : 'linear-gradient(135deg, #F59E0B, #D97706)',
                color: '#fff',
                cursor: (recallLoading || !recallAmount || Number(recallAmount) <= 0) ? 'not-allowed' : 'pointer',
                fontSize: 14,
                fontWeight: 600,
                transition: 'all 0.15s ease',
              }}
            >
              {recallLoading ? 'Recalling...' : 'Recall'}
            </button>
          </div>
        </div>
      </Modal>

      {/* ═══ CREATED API KEY MODAL ═══ */}
      <Modal
        open={showCreatedKeyModal}
        title={null}
        onCancel={() => setShowCreatedKeyModal(false)}
        footer={null}
        width={480}
      >
        <div style={{ textAlign: 'center', padding: '12px 0 8px' }}>
          <div style={{ width: 48, height: 48, borderRadius: 14, background: 'linear-gradient(135deg, #10B981, #059669)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
            <Key size={22} color="#fff" />
          </div>
          <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 4 }}>API Key Created</div>
          <p style={{ fontSize: 13, color: colors.textSecondary, marginBottom: 20 }}>
            Copy this key now. You won't be able to see it again.
          </p>
        </div>
        <div style={{ background: `${colors.text}06`, border: `1px solid ${colors.border}`, borderRadius: 10, padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <code style={{ flex: 1, fontSize: 12, wordBreak: 'break-all', color: colors.text, fontFamily: 'monospace' }}>
            {createdSubKey}
          </code>
          <button
            onClick={() => {
              if (createdSubKey) {
                navigator.clipboard.writeText(createdSubKey);
                message.success('Copied');
              }
            }}
            style={{ width: 34, height: 34, borderRadius: 8, border: `1px solid ${colors.border}`, background: 'transparent', color: colors.textSecondary, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
          >
            <Copy size={14} />
          </button>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button type="primary" onClick={() => setShowCreatedKeyModal(false)} style={{ borderRadius: 8, background: '#6366F1', borderColor: '#6366F1' }}>
            Done
          </Button>
        </div>
      </Modal>

      {/* ═══ TRANSFER HISTORY MODAL ═══ */}
      <Modal
        open={transfersModalOpen}
        title={null}
        onCancel={() => setTransfersModalOpen(false)}
        footer={null}
        width={600}
      >
        <div style={{ padding: '8px 0' }}>
          <div style={{ textAlign: 'center', marginBottom: 20 }}>
            <div style={{ width: 44, height: 44, borderRadius: 12, background: 'linear-gradient(135deg, #6366F1, #8B5CF6)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
              <Clock size={20} color="#fff" />
            </div>
            <div style={{ fontSize: 17, fontWeight: 600 }}>Transfer History</div>
          </div>

          {transfersLoading ? (
            <div style={{ textAlign: 'center', padding: 32, color: colors.textSecondary, fontSize: 13 }}>Loading...</div>
          ) : (Array.isArray(transfers) ? transfers : []).length === 0 ? (
            <div style={{ textAlign: 'center', padding: '32px 16px', color: colors.textSecondary }}>
              <Clock size={24} style={{ opacity: 0.4, marginBottom: 8 }} />
              <div style={{ fontSize: 14 }}>No transfers yet</div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 400, overflow: 'auto' }}>
              {(Array.isArray(transfers) ? transfers : []).map((t, i) => (
                <div
                  key={t.transfer_id || i}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '10px 14px',
                    borderRadius: 10,
                    border: `1px solid ${colors.border}`,
                  }}
                >
                  <div
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: 8,
                      background: t.transfer_type === 'allocation' ? 'rgba(16,185,129,0.1)' : 'rgba(245,158,11,0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    {t.transfer_type === 'allocation' ? <Send size={14} color="#10B981" /> : <ArrowDownLeft size={14} color="#F59E0B" />}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 500, color: colors.text }}>
                      {t.transfer_type === 'allocation' ? 'Allocated' : 'Recalled'} {Number(t.amount || 0).toFixed(2)} URO
                    </div>
                    <div style={{ fontSize: 11, color: colors.textSecondary, marginTop: 2 }}>
                      {t.description || (() => {
                        const list = Array.isArray(subAccounts) ? subAccounts : [];
                        const fromName = list.find((s) => s.client_id === t.from_client_id)?.client_name || t.from_client_id;
                        const toName = list.find((s) => s.client_id === t.to_client_id)?.client_name || t.to_client_id;
                        return `${fromName} → ${toName}`;
                      })()}
                    </div>
                  </div>
                  <div style={{ fontSize: 11, color: colors.textSecondary, whiteSpace: 'nowrap' }}>
                    {t.created_at ? new Date(t.created_at).toLocaleDateString() : ''}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Modal>

      <style>{`
        @keyframes sub-shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        .sub-row:hover {
          border-color: ${colors.text}20 !important;
          background: ${colors.text}03 !important;
        }
        .sub-icon-btn:hover {
          background: ${colors.text}08 !important;
        }
        .sub-action-btn:hover {
          background: ${colors.text}06 !important;
        }
      `}</style>
    </div>
  );
}

export default SubAccountsScreen;
